import { useEffect, useRef } from 'react';

import type { UnsplashImage } from '@/components/asset-manager/types';
import { Button } from '@/components/ui/button';
import { DestructiveText, MutedText, Paragraph } from '@/components/ui/typography';
import { EmptyState } from './empty-state';
import { DEFAULT_CATEGORY } from './unsplash/hooks/use-category-search';
import { useUnsplashImages } from './unsplash/hooks/use-unsplash-images';
import { ImageCard } from './unsplash/image-card';
import { SearchBar } from './unsplash/search-bar';
import { UnsplashGridSkeleton } from './unsplash/unsplash-grid-skeleton';

interface SuggestionsContentProps {
  selectedImageId: string | null;
  onImageSelect: (image: UnsplashImage) => void;
  onImageDoubleClick?: (image: UnsplashImage) => void;
  onSelectionClear?: () => void;
  isSelecting?: boolean;
}

export function SuggestionsContent({
  selectedImageId,
  onImageSelect,
  onImageDoubleClick,
  onSelectionClear,
  isSelecting = false,
}: SuggestionsContentProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    images,
    searchInput,
    setSearchInput,
    isLoading,
    isLoadingMore,
    error,
    sentinelRef,
    retry,
  } = useUnsplashImages({
    defaultQuery: DEFAULT_CATEGORY,
    scrollContainerRef,
  });

  useEffect(() => {
    if (!selectedImageId || isLoading) return;
    if (images.some((image) => image.id === selectedImageId)) return;
    onSelectionClear?.();
  }, [selectedImageId, images, isLoading, onSelectionClear]);

  const renderBody = () => {
    if (error && images.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <DestructiveText className="text-lg">{error}</DestructiveText>
          <Button variant="link" onClick={retry} className="mt-4">
            Try again
          </Button>
        </div>
      );
    }

    if (isLoading && images.length === 0) return <UnsplashGridSkeleton />;

    if (images.length === 0) {
      const trimmed = searchInput.trim();
      return (
        <EmptyState message={trimmed ? `No images found for "${trimmed}"` : 'No images found'} />
      );
    }

    return (
      <>
        <div className="columns-1 gap-2 sm:columns-2">
          {images.map((image) => (
            <ImageCard
              key={image.id}
              image={image}
              mode="select"
              isSelected={selectedImageId === image.id}
              isSelecting={isSelecting}
              onSelect={onImageSelect}
              onDoubleSelect={onImageDoubleClick}
              isDownloading={false}
            />
          ))}
        </div>

        <div ref={sentinelRef} />
        {isLoadingMore && (
          <div className="flex justify-center py-4">
            <MutedText>Loading more...</MutedText>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <div className="flex items-center justify-between gap-4">
          <Paragraph className="font-semibold">Stock images suggested for your page</Paragraph>
          <Paragraph className="text-xs text-foreground">
            Images by <span className="font-semibold">Unsplash</span>
          </Paragraph>
        </div>
      </div>
      <SearchBar
        value={searchInput}
        onChange={(value) => {
          if (selectedImageId) onSelectionClear?.();
          setSearchInput(value);
        }}
        placeholder="or search Unsplash"
        className="w-full md:w-full"
      />
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto p-1">
        {renderBody()}
      </div>
    </div>
  );
}
